import {
  Box,
  ClickAwayListener,
  Fade,
  Grid,
  Link as MuiLink,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { useCatalogMenu } from '@/hooks/use-catalog-menu';
import { useNavigate } from '@/hooks/use-navigation';
import { Features, type Feature } from '@/models/bootstrap/plan';

const Link = styled(MuiLink)(({ theme }) => ({
  color: theme.palette.text.primary,
  display: 'block',
  textDecoration: 'none',
  cursor: 'pointer',
  '&:hover': {
    color: theme.palette.primary.dark,
  },
}));

const CATS_PER_COL = 12;

const chunk = <T,>(arr: T[], size: number) =>
  Array.from({ length: Math.ceil(arr?.length / size) }, (_, i) =>
    arr.slice(i * size, i * size + size)
  );

interface CategoriesMenuProps {
  features: Feature[];
  onClose: () => void;
}

export const CategoriesMenu = ({ features, onClose }: CategoriesMenuProps) => {
  const navigate = useNavigate();
  const { courseTypes, knowledgeAreas } = useCatalogMenu();

  const featureItems = [
    {
      condition: features.some((f) => f.name === Features.Einstein),
      item: {
        id: 'einstein',
        label: 'Albert Einstein',
        route: '/cursos/?institutions=383',
      },
    },
    {
      condition: features.some((f) => f.name === Features.FullIncluded),
      item: {
        id: 'full-include',
        label: 'Cursos Gratuitos',
        route: '/cursos/?free_courses=true',
      },
    },
  ];

  const courseTypesItems = [
    ...featureItems.filter((f) => f.condition).map((f) => f.item),
    ...courseTypes,
  ];

  const columns = [courseTypesItems, ...chunk(knowledgeAreas, CATS_PER_COL)];
  const totalCols = columns.length;

  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    route: string
  ) => {
    e.preventDefault();
    navigate(route);
    onClose();
  };

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Fade in timeout={400}>
        <Stack
          direction={{ sm: 'column', md: 'row' }}
          gridColumn={{ xs: 1, sm: totalCols }}
          justifyContent="space-between"
          spacing={4}
          py={3}
        >
          {columns.map((col, idx) => (
            <Grid sx={{ xs: 12 }} key={idx}>
              {idx === 0 && (
                <Typography
                  textTransform="uppercase"
                  fontSize={12}
                  color="neutral.500"
                  fontWeight="semibold"
                  mb={1}
                >
                  Categorias
                </Typography>
              )}
              {idx === 1 && (
                <Typography
                  textTransform="uppercase"
                  fontSize={12}
                  color="neutral.500"
                  fontWeight="semibold"
                  mb={1}
                >
                  Temas
                </Typography>
              )}
              <Box mt={idx > 1 ? 4 : 0}>
                {col?.map((item) => (
                  <Link
                    key={item.id}
                    href={item.route}
                    sx={{
                      fontSize: idx === 0 ? 16 : 14,
                      fontWeight: idx === 0 ? 'semibold' : 'normal',
                      mb: idx === 0 ? 2 : 1,
                    }}
                    onClick={(e) => handleClick(e, item.route)}
                  >
                    {item.label}
                  </Link>
                ))}
              </Box>
            </Grid>
          ))}
        </Stack>
      </Fade>
    </ClickAwayListener>
  );
};
